import {
  BELABOX_MODULE_ID,
  BELABOX_SECRET_UNAVAILABLE_STATUS_CODE,
  BELABOX_STREAM_HISTORY_LIMIT,
  BELABOX_STATS_URL_SECRET,
  type BelaboxFetchFailureReason,
  type BelaboxPhase,
  type BelaboxStatusErrorCode,
  type BelaboxSample,
  type BelaboxHistoryPoint,
  type BelaboxStreamSummary,
} from "../contracts";
import {
  BELABOX_LOW_BITRATE_KBPS,
  bitrateP10,
  elapsedSampleSeconds,
} from "../domain/history";
import {
  createInitialAlertState,
  type BelaboxAlertIdempotencyKind,
  type BelaboxAlertMessageKind,
  type BelaboxAlertState,
} from "../domain/alert";

export interface BelaboxRecentPoint {
  at: string;
  bitrateKbps: number;
  rttMs: number;
  connected: boolean;
}

export interface BelaboxFetchPhase {
  consecutiveFailures: number;
  failing: boolean;
  lastAttemptAt?: string;
}

export interface BelaboxStatus {
  sample: BelaboxSample | null;
  historySample: BelaboxSample | null;
  historyModuleRevision: number | null;
  errorCode: BelaboxStatusErrorCode | null;
  polling: boolean;
  streamId: string | null;
  streamSessionKey: string | null;
  belaboxStreamId: string | null;
  fetchPhase: BelaboxFetchPhase;
  alertState: BelaboxAlertState;
  recent: readonly BelaboxRecentPoint[];
  revision: number;
}

export interface BelaboxStreamStateSnapshot {
  state: string;
  changedAt: string;
  startedAt: string | null;
  streamId: string | null;
}

export interface BelaboxStreamSession extends BelaboxStreamStateSnapshot {
  state: "online" | "offline";
}

export const getBelaboxStreamStateSnapshot = async (
  db: D1Database,
  channelId: string,
): Promise<BelaboxStreamStateSnapshot | null> => {
  const row = await db.prepare(
    `SELECT state, changed_at, started_at, stream_id
       FROM channel_stream_state WHERE channel_id = ?`,
  ).bind(channelId).first<{
    state: string;
    changed_at: string;
    started_at: string | null;
    stream_id: string | null;
  }>();
  return row === null ? null : {
    state: row.state,
    changedAt: row.changed_at,
    startedAt: row.started_at,
    streamId: row.stream_id,
  };
};

export const sameBelaboxStreamStateSnapshot = (
  left: BelaboxStreamStateSnapshot | null,
  right: BelaboxStreamStateSnapshot | null,
): boolean => left === null ? right === null : right !== null &&
  left.state === right.state && left.changedAt === right.changedAt &&
  left.startedAt === right.startedAt && left.streamId === right.streamId;

export const belaboxStreamStateSnapshotGuard = `(
  (? = 1 AND NOT EXISTS (
    SELECT 1 FROM channel_stream_state WHERE channel_id = ?
  )) OR (? = 0 AND EXISTS (
    SELECT 1 FROM channel_stream_state
     WHERE channel_id = ? AND state = ? AND changed_at = ?
       AND started_at IS ? AND stream_id IS ?
  ))
)`;

const streamSnapshotBindings = (
  channelId: string,
  snapshot: BelaboxStreamStateSnapshot | null,
): unknown[] => [
  Number(snapshot === null), channelId,
  Number(snapshot === null), channelId,
  snapshot?.state ?? null,
  snapshot?.changedAt ?? null,
  snapshot?.startedAt ?? null,
  snapshot?.streamId ?? null,
];

interface BelaboxStatusRow {
  sampled_at: string | null;
  sample_json: string | null;
  history_sample_json: string | null;
  history_module_revision: number | null;
  error_code: string | null;
  polling: number;
  stream_id: string | null;
  stream_session_key: string | null;
  belabox_stream_id: string | null;
  fetch_phase_json: string;
  recent_json: string;
  alert_json: string;
  revision: number;
}

interface BelaboxStreamSessionRow {
  state: "online" | "offline";
  changed_at: string;
  started_at: string | null;
  stream_id: string | null;
}

const EMPTY_FETCH_PHASE: BelaboxFetchPhase = { consecutiveFailures: 0, failing: false };

const finiteNonnegative = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value) && value >= 0;

const isBelaboxPhase = (value: unknown): value is BelaboxPhase =>
  value === "healthy" || value === "low" || value === "disconnected" || value === "inactive";

const parseSample = (value: string | null): BelaboxSample | null => {
  if (value === null) return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return null;
    const sample = parsed as Readonly<Record<string, unknown>>;
    if (typeof sample.at !== "string" || typeof sample.connected !== "boolean" ||
        !finiteNonnegative(sample.bitrateKbps) || !finiteNonnegative(sample.rttMs) ||
        !finiteNonnegative(sample.latencyMs) || !finiteNonnegative(sample.network) ||
        !finiteNonnegative(sample.droppedPackets)) return null;
    if (sample.droppedTotal !== undefined && !finiteNonnegative(sample.droppedTotal) ||
        sample.phase !== undefined && !isBelaboxPhase(sample.phase) ||
        sample.alertStartedAt !== undefined && sample.alertStartedAt !== null && typeof sample.alertStartedAt !== "string") {
      return null;
    }
    return sample as unknown as BelaboxSample;
  } catch {
    return null;
  }
};

const parseFetchPhase = (value: string): BelaboxFetchPhase => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return EMPTY_FETCH_PHASE;
    const state = parsed as Readonly<Record<string, unknown>>;
    return Number.isSafeInteger(state.consecutiveFailures) && Number(state.consecutiveFailures) >= 0 &&
      typeof state.fetchFailing === "boolean"
      ? {
        consecutiveFailures: Number(state.consecutiveFailures),
        failing: state.fetchFailing,
        ...(typeof state.lastAttemptAt === "string" && Number.isFinite(Date.parse(state.lastAttemptAt))
          ? { lastAttemptAt: state.lastAttemptAt }
          : {}),
      }
      : EMPTY_FETCH_PHASE;
  } catch {
    return EMPTY_FETCH_PHASE;
  }
};

const parseRecent = (value: string): readonly BelaboxRecentPoint[] => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((entry): BelaboxRecentPoint[] => {
      if (!Array.isArray(entry) || entry.length !== 4 || typeof entry[0] !== "string" ||
          !finiteNonnegative(entry[1]) || !finiteNonnegative(entry[2]) || typeof entry[3] !== "boolean") return [];
      return [{ at: entry[0], bitrateKbps: entry[1], rttMs: entry[2], connected: entry[3] }];
    });
  } catch {
    return [];
  }
};

const isAlertPhase = (value: unknown): value is BelaboxAlertState["phase"] =>
  value === "ok" || value === "pending" || value === "alarm" || value === "recovering";
const isAlertKind = (value: unknown): value is NonNullable<BelaboxAlertState["kind"]> =>
  value === "low" || value === "disconnect";

const parseAlertState = (value: string): BelaboxAlertState => {
  try {
    const parsed: unknown = JSON.parse(value);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) return createInitialAlertState();
    const state = parsed as Readonly<Record<string, unknown>>;
    if (!isAlertPhase(state.phase) || (state.kind !== null && !isAlertKind(state.kind)) ||
        (state.since !== null && typeof state.since !== "string") ||
        (state.episodeStartedAt !== null && typeof state.episodeStartedAt !== "string") ||
        (state.completedEpisodeAt !== null && typeof state.completedEpisodeAt !== "string") ||
        (state.lastChatSentAt !== null && typeof state.lastChatSentAt !== "string") ||
        typeof state.chatSentInEpisode !== "boolean") return createInitialAlertState();

    let pendingChat: BelaboxAlertState["pendingChat"] = null;
    if (typeof state.pendingChat === "object" && state.pendingChat !== null && !Array.isArray(state.pendingChat)) {
      const candidate = state.pendingChat as Readonly<Record<string, unknown>>;
      const validMessageKind = candidate.kind === "low" || candidate.kind === "disconnect" || candidate.kind === "recovery";
      const validIdempotencyKind = candidate.idempotencyKind === "alert" || candidate.idempotencyKind === "escalate" || candidate.idempotencyKind === "recovered";
      const validPhase = candidate.validPhase === "alarm" || candidate.validPhase === "ok";
      if (validMessageKind && validIdempotencyKind && typeof candidate.episodeStartedAt === "string" &&
          isAlertKind(candidate.alertKind) && validPhase && finiteNonnegative(candidate.threshold) &&
          Number.isSafeInteger(candidate.seconds) && Number(candidate.seconds) >= 0) {
        pendingChat = {
          kind: candidate.kind as BelaboxAlertMessageKind,
          idempotencyKind: candidate.idempotencyKind as BelaboxAlertIdempotencyKind,
          episodeStartedAt: candidate.episodeStartedAt,
          alertKind: candidate.alertKind,
          validPhase: candidate.validPhase as "alarm" | "ok",
          threshold: candidate.threshold,
          seconds: Number(candidate.seconds),
        };
      }
    }
    return {
      phase: state.phase,
      kind: state.kind,
      since: state.since,
      episodeStartedAt: state.episodeStartedAt,
      completedEpisodeAt: state.completedEpisodeAt,
      lastChatSentAt: state.lastChatSentAt,
      chatSentInEpisode: state.chatSentInEpisode,
      pendingChat,
    };
  } catch {
    return createInitialAlertState();
  }
};

const isStatusErrorCode = (value: string | null): value is BelaboxStatusErrorCode =>
  value !== null && [
    "timeout", "network", "http_4xx", "http_5xx", "redirect_rejected", "too_large", "malformed", "budget_exhausted",
    BELABOX_SECRET_UNAVAILABLE_STATUS_CODE,
  ].includes(value);

export const getBelaboxStatus = async (db: D1Database, channelId: string): Promise<BelaboxStatus | null> => {
  const row = await db.prepare(
    `SELECT sampled_at, sample_json, history_sample_json, history_module_revision,
            error_code, polling, stream_id, stream_session_key, belabox_stream_id, fetch_phase_json, recent_json,
            alert_json, revision
       FROM belabox_status WHERE channel_id = ?`,
  ).bind(channelId).first<BelaboxStatusRow>();
  if (row === null) return null;
  return {
    sample: parseSample(row.sample_json),
    historySample: parseSample(row.history_sample_json),
    historyModuleRevision: row.history_module_revision,
    errorCode: isStatusErrorCode(row.error_code) ? row.error_code : null,
    polling: row.polling === 1,
    streamId: row.stream_id,
    streamSessionKey: row.stream_session_key,
    belaboxStreamId: row.belabox_stream_id,
    fetchPhase: parseFetchPhase(row.fetch_phase_json),
    recent: parseRecent(row.recent_json),
    alertState: parseAlertState(row.alert_json),
    revision: row.revision,
  };
};

export const getBelaboxStreamSession = async (
  db: D1Database,
  channelId: string,
): Promise<BelaboxStreamSession | null> => {
  const row = await db.prepare(
    `SELECT state, changed_at, started_at, stream_id
       FROM channel_stream_state
      WHERE channel_id = ?`,
  ).bind(channelId).first<BelaboxStreamSessionRow>();
  return row === null ? null : {
    state: row.state,
    changedAt: row.changed_at,
    startedAt: row.started_at,
    streamId: row.stream_id,
  };
};

export const belaboxStreamSessionKey = (session: BelaboxStreamSession | null): string | null => {
  if (session === null) return null;
  if (session.state === "offline") return `offline:${session.changedAt}`;
  if (session.streamId !== null) return `stream:${session.streamId}`;
  if (session.startedAt !== null) return `started:${session.startedAt}:${session.changedAt}`;
  return `online:${session.changedAt}`;
};

export const sameBelaboxStreamSession = (
  left: BelaboxStreamSession | null,
  right: BelaboxStreamSession | null,
): boolean => belaboxStreamSessionKey(left) === belaboxStreamSessionKey(right);

export const belaboxStatusMatchesSession = (
  status: BelaboxStatus | null,
  session: BelaboxStreamSession | null,
): boolean => {
  if (status === null) return false;
  if (session === null) return status.streamId === null && status.streamSessionKey === null;
  const sessionKey = belaboxStreamSessionKey(session);
  return status.streamSessionKey === sessionKey || (
    status.streamSessionKey === null && session.state === "online" && session.streamId !== null &&
    status.streamId === session.streamId
  );
};

export const belaboxStreamSessionGuard = (
  channelId: string,
  session: BelaboxStreamSession | null | undefined,
): { sql: string; values: readonly (string | null)[] } => {
  if (session === undefined) return { sql: "", values: [] };
  if (session === null) {
    return {
      sql: "AND NOT EXISTS (SELECT 1 FROM channel_stream_state WHERE channel_id = ?)",
      values: [channelId],
    };
  }
  if (session.state === "offline") {
    return {
      sql: "AND EXISTS (SELECT 1 FROM channel_stream_state WHERE channel_id = ? AND state = 'offline' AND changed_at = ?)",
      values: [channelId, session.changedAt],
    };
  }
  if (session.streamId !== null) {
    return {
      sql: "AND EXISTS (SELECT 1 FROM channel_stream_state WHERE channel_id = ? AND state = 'online' AND stream_id = ?)",
      values: [channelId, session.streamId],
    };
  }
  if (session.startedAt !== null) {
    return {
      sql: "AND EXISTS (SELECT 1 FROM channel_stream_state WHERE channel_id = ? AND state = 'online' AND stream_id IS NULL AND started_at IS ? AND changed_at = ?)",
      values: [channelId, session.startedAt, session.changedAt],
    };
  }
  return {
    sql: "AND EXISTS (SELECT 1 FROM channel_stream_state WHERE channel_id = ? AND state = 'online' AND stream_id IS NULL AND started_at IS NULL AND changed_at = ?)",
    values: [channelId, session.changedAt],
  };
};

export const getBelaboxLiveHistory = async (db: D1Database, channelId: string, now = Date.now()): Promise<BelaboxHistoryPoint[]> => {
  const status = await getBelaboxStatus(db, channelId);
  if (status === null) return [];
  const cutoff = now - 10 * 60_000;
  return status.recent.flatMap(({ at, bitrateKbps, connected }) => {
    const timestamp = Date.parse(at);
    return Number.isFinite(timestamp) && timestamp >= cutoff
      ? [[timestamp, bitrateKbps, Number(connected)] as const]
      : [];
  });
};

export const getBelaboxStreamHistory = async (
  db: D1Database,
  channelId: string,
  streamId: string,
): Promise<BelaboxHistoryPoint[]> => {
  const result = await db.prepare(
    `SELECT minute_at, samples, connected_samples, bitrate_sum
       FROM belabox_minutes
      WHERE channel_id = ? AND stream_id = ?
      ORDER BY minute_at ASC`,
  ).bind(channelId, streamId).all<{
    minute_at: string;
    samples: number;
    connected_samples: number;
    bitrate_sum: number;
  }>();
  return result.results.flatMap((row) => {
    const timestamp = Date.parse(row.minute_at);
    if (!Number.isFinite(timestamp) || !Number.isFinite(row.samples) || row.samples < 1 ||
        !Number.isFinite(row.connected_samples) || !Number.isFinite(row.bitrate_sum)) return [];
    return [[timestamp, row.bitrate_sum / row.samples, row.connected_samples / row.samples] as const];
  });
};

export const listBelaboxStreams = async (db: D1Database, channelId: string): Promise<BelaboxStreamSummary[]> => {
  const result = await db.prepare(
    `SELECT stream_id, started_at, ended_at, samples, bitrate_avg, bitrate_p10,
            low_seconds, disconnected_seconds, disconnect_count, dropped_total
       FROM belabox_streams
      WHERE channel_id = ?
      ORDER BY started_at DESC
      LIMIT ?`,
  ).bind(channelId, BELABOX_STREAM_HISTORY_LIMIT).all<{
    stream_id: string;
    started_at: string;
    ended_at: string | null;
    samples: number;
    bitrate_avg: number;
    bitrate_p10: number | null;
    low_seconds: number;
    disconnected_seconds: number;
    disconnect_count: number;
    dropped_total: number;
  }>();
  return result.results.map((row) => ({
    streamId: row.stream_id,
    startedAt: row.started_at,
    endedAt: row.ended_at,
    samples: row.samples,
    bitrateAvg: row.bitrate_avg,
    bitrateP10: row.bitrate_p10,
    lowSeconds: row.low_seconds,
    disconnectedSeconds: row.disconnected_seconds,
    disconnectCount: row.disconnect_count,
    droppedTotal: row.dropped_total,
  }));
};

export const belaboxHistoryModuleRevisionGuard = `(
  (? IS NULL AND NOT EXISTS (
    SELECT 1 FROM channel_modules WHERE channel_id = ? AND module_id = 'belabox'
  )) OR EXISTS (
    SELECT 1 FROM channel_modules WHERE channel_id = ? AND module_id = 'belabox' AND revision = ?
  )
)`;

export const prepareBelaboxHistoryFinalizers = async (
  db: D1Database,
  channelId: string,
  endedAt: string,
  expectedModuleRevision: number | null,
  expectedStreamSnapshot: BelaboxStreamStateSnapshot | null,
  options: {
    maxBaselineAgeMs?: number;
    lowBitrateKbps?: number;
    excludeStreamId?: string | null;
    expectedStatusRevision: number | null;
    expectedStatusStreamId: string | null;
    expectedStatusSessionKey: string | null;
    expectedSecretVersion?: string;
    expectedModuleMode?: "interval" | "on_demand";
    finalizeNoncurrentStreams?: boolean;
  },
): Promise<D1PreparedStatement[]> => {
  const [openStreams, status] = await Promise.all([
    db.prepare(
      `SELECT stream_id, started_at FROM belabox_streams
        WHERE channel_id = ? AND ended_at IS NULL
          AND (? IS NULL OR stream_id IS NOT ?)`,
    ).bind(channelId, options.excludeStreamId ?? null, options.excludeStreamId ?? null)
      .all<{ stream_id: string; started_at: string }>(),
    getBelaboxStatus(db, channelId),
  ]);
  const expectedOpenStreamSetGuard = options.finalizeNoncurrentStreams === true
    ? belaboxExpectedOpenStreamSetGuard(
      channelId,
      options.excludeStreamId ?? null,
      openStreams.results,
    )
    : { sql: "", values: [] };
  const ending = Date.parse(endedAt);
  const normalizedEndedAt = Number.isFinite(ending) ? new Date(ending).toISOString() : new Date().toISOString();
  const normalizedEnding = Date.parse(normalizedEndedAt);
  return await Promise.all(openStreams.results.map(async (stream) => {
    const minutes = await db.prepare(
      `SELECT samples, bitrate_sum FROM belabox_minutes
        WHERE channel_id = ? AND stream_id = ?
        ORDER BY minute_at ASC`,
    ).bind(channelId, stream.stream_id).all<{ samples: number; bitrate_sum: number }>();
    const p10 = bitrateP10(minutes.results.flatMap((row) =>
      Number.isFinite(row.samples) && row.samples > 0 && Number.isFinite(row.bitrate_sum)
        ? [row.bitrate_sum / row.samples]
        : []));
    const sample = status?.streamId === stream.stream_id ? status.historySample : null;
    const sampleAt = sample === null ? Number.NaN : Date.parse(sample.at);
    const streamEnding = Number.isFinite(sampleAt) ? Math.max(normalizedEnding, sampleAt) : normalizedEnding;
    const streamEndedAt = new Date(streamEnding).toISOString();
    const sampleAgeMs = sample === null ? Number.POSITIVE_INFINITY : streamEnding - sampleAt;
    const tailIsCurrent = sample !== null && Number.isFinite(sampleAgeMs) && sampleAgeMs >= 0 &&
      sampleAgeMs <= (options.maxBaselineAgeMs ?? 0);
    const tailSeconds = tailIsCurrent ? elapsedSampleSeconds(sample, { ...sample, at: streamEndedAt }) : 0;
    const lowTail = sample?.connected === true &&
      sample.bitrateKbps < (options.lowBitrateKbps ?? BELABOX_LOW_BITRATE_KBPS) ? tailSeconds : 0;
    const disconnectedTail = sample?.connected === false ? tailSeconds : 0;
    return db.prepare(
      `UPDATE belabox_streams
          SET ended_at = ?,
              bitrate_p10 = ?,
              low_seconds = low_seconds + ?,
              disconnected_seconds = disconnected_seconds + ?
        WHERE channel_id = ? AND stream_id = ? AND started_at = ? AND ended_at IS NULL
          AND ${belaboxHistoryModuleRevisionGuard}
          AND ${belaboxStreamStateSnapshotGuard}
          AND ((? IS NULL AND NOT EXISTS (
            SELECT 1 FROM belabox_status WHERE channel_id = ?
          )) OR EXISTS (
            SELECT 1 FROM belabox_status
             WHERE channel_id = ? AND revision = ? AND stream_id IS ? AND stream_session_key IS ?
          ))
          AND (? IS NULL OR EXISTS (
            SELECT 1 FROM module_secrets
             WHERE channel_id = ? AND module_id = 'belabox' AND name = 'stats_url' AND ciphertext = ?
          ))
          AND (? IS NULL OR EXISTS (
            SELECT 1 FROM channel_modules
             WHERE channel_id = ? AND module_id = 'belabox' AND enabled = 1 AND revision = ?
               AND json_extract(settings, '$.mode') = ?
          ))
          ${expectedOpenStreamSetGuard.sql}`,
    ).bind(
      streamEndedAt,
      p10,
      lowTail,
      disconnectedTail,
      channelId,
      stream.stream_id,
      stream.started_at,
      expectedModuleRevision,
      channelId,
      channelId,
      expectedModuleRevision,
      ...streamSnapshotBindings(channelId, expectedStreamSnapshot),
      options.expectedStatusRevision,
      channelId,
      channelId,
      options.expectedStatusRevision,
      options.expectedStatusStreamId,
      options.expectedStatusSessionKey,
      options.expectedSecretVersion ?? null,
      channelId,
      options.expectedSecretVersion ?? null,
      options.expectedModuleMode ?? null,
      channelId,
      expectedModuleRevision,
      options.expectedModuleMode ?? "interval",
      ...expectedOpenStreamSetGuard.values,
    );
  }));
};

/** Guards an adoption batch against noncurrent summaries opened after its inventory read. */
export const belaboxExpectedOpenStreamSetGuard = (
  channelId: string,
  excludedStreamId: string | null,
  openStreams: readonly { stream_id: string; started_at: string }[],
): { sql: string; values: unknown[] } => ({
  sql: `AND NOT EXISTS (
    SELECT 1 FROM belabox_streams AS pending_stream
     WHERE pending_stream.channel_id = ? AND pending_stream.ended_at IS NULL
       AND pending_stream.stream_id IS NOT ?
       AND NOT EXISTS (
         SELECT 1 FROM json_each(?) AS expected_stream
          WHERE json_extract(expected_stream.value, '$[0]') = pending_stream.stream_id
            AND json_extract(expected_stream.value, '$[1]') = pending_stream.started_at
       )
  )`,
  values: [
    channelId,
    excludedStreamId,
    JSON.stringify(openStreams.map(({ stream_id, started_at }) => [stream_id, started_at])),
  ],
});

/** Finalizes summaries and resets their sample baseline in one revision-guarded D1 batch. */
export const finalizeAndResetBelaboxHistory = async (
  db: D1Database,
  channelId: string,
  endedAt: string,
  expectedModuleRevision: number | null,
  expectedStreamSnapshot: BelaboxStreamStateSnapshot | null,
  options: {
    finalizeOpen?: boolean;
    resetStream?: boolean;
    stopPolling?: boolean;
    errorCode?: BelaboxStatusErrorCode;
    maxBaselineAgeMs?: number;
    lowBitrateKbps?: number;
  } = {},
): Promise<boolean> => {
  const status = await getBelaboxStatus(db, channelId);
  const ending = Date.parse(endedAt);
  const normalizedEndedAt = Number.isFinite(ending) ? new Date(ending).toISOString() : new Date().toISOString();
  const finalizers = options.finalizeOpen === false ? [] : await prepareBelaboxHistoryFinalizers(
    db,
    channelId,
    normalizedEndedAt,
    expectedModuleRevision,
    expectedStreamSnapshot,
    {
      ...(options.maxBaselineAgeMs === undefined ? {} : { maxBaselineAgeMs: options.maxBaselineAgeMs }),
      ...(options.lowBitrateKbps === undefined ? {} : { lowBitrateKbps: options.lowBitrateKbps }),
      expectedStatusRevision: status?.revision ?? null,
      expectedStatusStreamId: status?.streamId ?? null,
      expectedStatusSessionKey: status?.streamSessionKey ?? null,
    },
  );
  const assignments = [
    ...(options.stopPolling === false ? [] : ["polling = 0"]),
    "history_sample_json = NULL",
    "history_module_revision = NULL",
    ...(options.errorCode === undefined || options.resetStream === true ? [] : ["error_code = ?"]),
    ...(options.resetStream === true ? [
      "error_code = NULL",
      "sampled_at = NULL",
      "sample_json = NULL",
      "stream_id = NULL",
      "stream_session_key = NULL",
      "belabox_stream_id = NULL",
      "fetch_phase_json = ?",
      "recent_json = '[]'",
      "alert_json = '{}'",
    ] : []),
    "revision = revision + 1",
  ];
  const changes = [
    ...(options.stopPolling === false ? [] : ["polling != 0"]),
    "history_sample_json IS NOT NULL",
    "history_module_revision IS NOT NULL",
    ...(options.resetStream === true ? [
      "error_code IS NOT NULL",
      "sampled_at IS NOT NULL",
      "sample_json IS NOT NULL",
      "stream_id IS NOT NULL",
      "stream_session_key IS NOT NULL",
      "belabox_stream_id IS NOT NULL",
      "recent_json != '[]'",
      "alert_json != '{}'",
      `fetch_phase_json != '${encodedPhase(EMPTY_FETCH_PHASE)}'`,
    ] : []),
    ...(options.errorCode === undefined || options.resetStream === true ? [] : ["error_code IS NOT ?"]),
  ];
  const bindings: unknown[] = [];
  if (options.errorCode !== undefined && options.resetStream !== true) bindings.push(options.errorCode);
  if (options.resetStream === true) bindings.push(encodedPhase(EMPTY_FETCH_PHASE));
  bindings.push(channelId);
  if (options.errorCode !== undefined && options.resetStream !== true) bindings.push(options.errorCode);
  bindings.push(
    expectedModuleRevision,
    channelId,
    channelId,
    expectedModuleRevision,
    ...streamSnapshotBindings(channelId, expectedStreamSnapshot),
  );
  const statusReset = db.prepare(
    `UPDATE belabox_status
        SET ${assignments.join(",\n            ")}
      WHERE channel_id = ? AND (${changes.join(" OR ")})
        AND ${belaboxHistoryModuleRevisionGuard}
        AND ${belaboxStreamStateSnapshotGuard}`,
  ).bind(...bindings);
  const statusSeed = options.errorCode === undefined ? [] : [db.prepare(
    `INSERT INTO belabox_status
      (channel_id, error_code, polling, fetch_phase_json, recent_json, revision)
     SELECT ?, ?, 0, ?, '[]', 1
      WHERE NOT EXISTS (SELECT 1 FROM belabox_status WHERE channel_id = ?)
        AND ${belaboxHistoryModuleRevisionGuard}
        AND ${belaboxStreamStateSnapshotGuard}`,
  ).bind(
    channelId,
    options.errorCode,
    encodedPhase(EMPTY_FETCH_PHASE),
    channelId,
    expectedModuleRevision,
    channelId,
    channelId,
    expectedModuleRevision,
    ...streamSnapshotBindings(channelId, expectedStreamSnapshot),
  )];
  await db.batch([...finalizers, ...statusSeed, statusReset]);
  const [currentModule, currentStreamSnapshot] = await Promise.all([
    db.prepare(
      "SELECT revision FROM channel_modules WHERE channel_id = ? AND module_id = 'belabox'",
    ).bind(channelId).first<{ revision: number }>(),
    getBelaboxStreamStateSnapshot(db, channelId),
  ]);
  return (currentModule?.revision ?? null) === expectedModuleRevision &&
    sameBelaboxStreamStateSnapshot(currentStreamSnapshot, expectedStreamSnapshot);
};

/** Reopens a previously finalized row only after the serialized poll alarm sees the live stream. */
export const reopenCurrentBelaboxStream = async (
  db: D1Database,
  channelId: string,
  expectedModuleRevision: number,
): Promise<boolean> => {
  const result = await db.prepare(
    `UPDATE belabox_streams
        SET ended_at = NULL, bitrate_p10 = NULL
      WHERE channel_id = ? AND ended_at IS NOT NULL
        AND EXISTS (
          SELECT 1 FROM channel_stream_state
           WHERE channel_id = belabox_streams.channel_id
             AND state = 'online'
             AND stream_id = belabox_streams.stream_id
        )
        AND EXISTS (
          SELECT 1 FROM channel_modules
           WHERE channel_id = ? AND module_id = 'belabox' AND enabled = 1
             AND revision = ? AND json_extract(settings, '$.mode') = 'interval'
        )`,
  ).bind(channelId, channelId, expectedModuleRevision).run();
  return result.meta.changes > 0;
};

export const belaboxStreamExists = async (
  db: D1Database,
  channelId: string,
  streamId: string,
): Promise<boolean> => (await db.prepare(
  "SELECT 1 AS present FROM belabox_streams WHERE channel_id = ? AND stream_id = ?",
).bind(channelId, streamId).first<{ present: number }>()) !== null;

const BELABOX_BITRATE_WINDOW_MS = 5 * 60_000;
const BELABOX_BITRATE_ROLLUP_WRITE_BUDGET = 2_000;
// An hourly run reserves about 48k of D1 Free's 100k daily row writes.
// Each window costs ~16 writes for five minute-row updates, its upsert and triggers,
// plus ~15 row deletes and delete-trigger writes when expired; indexes add load.
// 2,000 / 31 caps a run at 64 windows.
const BELABOX_BITRATE_ROLLUP_MAX_WINDOWS = Math.floor(BELABOX_BITRATE_ROLLUP_WRITE_BUDGET / 31);
const BELABOX_BITRATE_ROLLUP_BATCH_STATEMENTS = 100;

interface BelaboxMinuteRollupWindow {
  channel_id: string;
  stream_id: string;
  window_start: number;
}

const rollupBelaboxMinuteWindows = async (db: D1Database, now: string): Promise<void> => {
  const nowMs = Date.parse(now);
  if (!Number.isFinite(nowMs)) return;
  const completedBeforeMs = Math.floor((nowMs - BELABOX_BITRATE_WINDOW_MS) / BELABOX_BITRATE_WINDOW_MS) *
    BELABOX_BITRATE_WINDOW_MS;
  const windows = await db.prepare(
    `WITH candidate_windows AS (
       SELECT CAST(strftime('%s', minute_at) AS INTEGER) / 300 * 300000 AS window_start,
              channel_id, stream_id
         FROM belabox_minutes
        WHERE rolled_up_at IS NULL
          AND CAST(strftime('%s', minute_at) AS INTEGER) / 300 * 300000 < ?
        GROUP BY window_start, channel_id, stream_id
        ORDER BY window_start, channel_id, stream_id
        LIMIT ?
     )
     SELECT channel_id, stream_id, window_start FROM candidate_windows`,
  ).bind(completedBeforeMs, BELABOX_BITRATE_ROLLUP_MAX_WINDOWS).all<BelaboxMinuteRollupWindow>();
  if (windows.results.length === 0) return;

  let batch: D1PreparedStatement[] = [];
  const flush = async (): Promise<void> => {
    if (batch.length === 0) return;
    await db.batch(batch);
    batch = [];
  };
  for (const window of windows.results) {
    const windowStartAt = new Date(window.window_start).toISOString();
    const windowEndAt = new Date(window.window_start + BELABOX_BITRATE_WINDOW_MS).toISOString();
    const aggregate = db.prepare(
      `WITH ranked_minutes AS (
         SELECT samples, bitrate_min, bitrate_max, bitrate_sum, rtt_sum, dropped_delta,
                bitrate_sum / samples AS minute_average,
                ROW_NUMBER() OVER (ORDER BY bitrate_sum / samples) AS minute_rank,
                COUNT(*) OVER () AS minute_count
           FROM belabox_minutes
          WHERE channel_id = ? AND stream_id = ?
            AND minute_at >= ? AND minute_at < ?
       ), window_metrics AS (
         SELECT MIN(bitrate_min) AS bitrate_min,
                MAX(bitrate_max) AS bitrate_max,
                SUM(bitrate_sum) / SUM(samples) AS bitrate_avg,
                MAX(CASE WHEN minute_rank = CAST((minute_count + 9) / 10 AS INTEGER)
                         THEN minute_average END) AS bitrate_p10,
                SUM(rtt_sum) / SUM(samples) AS rtt_avg,
                SUM(dropped_delta) AS dropped_delta,
                SUM(samples) AS sample_count
           FROM ranked_minutes
       )
       INSERT INTO belabox_bitrate_windows
        (channel_id, stream_id, window_start, bitrate_min, bitrate_max, bitrate_avg, bitrate_p10,
         rtt_avg, dropped_delta, sample_count)
       SELECT ?, ?, ?, bitrate_min, bitrate_max, bitrate_avg, bitrate_p10,
              rtt_avg, dropped_delta, sample_count
         FROM window_metrics
        WHERE sample_count > 0
       ON CONFLICT (channel_id, stream_id, window_start) DO UPDATE SET
         bitrate_min = excluded.bitrate_min,
         bitrate_max = excluded.bitrate_max,
         bitrate_avg = excluded.bitrate_avg,
         bitrate_p10 = excluded.bitrate_p10,
         rtt_avg = excluded.rtt_avg,
         dropped_delta = excluded.dropped_delta,
         sample_count = excluded.sample_count`,
    ).bind(
      window.channel_id,
      window.stream_id,
      windowStartAt,
      windowEndAt,
      window.channel_id,
      window.stream_id,
      window.window_start,
    );
    const markSourceRows = db.prepare(
      `UPDATE belabox_minutes SET rolled_up_at = ?
        WHERE rolled_up_at IS NULL AND channel_id = ? AND stream_id = ?
          AND minute_at >= ? AND minute_at < ?`,
    ).bind(now, window.channel_id, window.stream_id, windowStartAt, windowEndAt);
    if (batch.length + 2 > BELABOX_BITRATE_ROLLUP_BATCH_STATEMENTS) await flush();
    batch.push(aggregate, markSourceRows);
  }
  await flush();
};

const purgeExpiredBelaboxMinutes = async (db: D1Database, now: string): Promise<void> => {
  const cutoff = new Date(Date.parse(now) - 30 * 24 * 60 * 60_000).toISOString();
  const channels = await db.prepare("SELECT channel_id FROM channels").all<{ channel_id: string }>();
  const batchSize = 50;
  for (let offset = 0; offset < channels.results.length; offset += batchSize) {
    const deletes = channels.results.slice(offset, offset + batchSize).map(({ channel_id }) =>
      db.prepare("DELETE FROM belabox_minutes WHERE channel_id = ? AND minute_at < ? AND rolled_up_at IS NOT NULL")
        .bind(channel_id, cutoff));
    await db.batch(deletes);
  }
};

export const maintainBelaboxHistory = async (db: D1Database, now: string): Promise<void> => {
  await rollupBelaboxMinuteWindows(db, now);
  await purgeExpiredBelaboxMinutes(db, now);
};

const encodedRecent = (recent: readonly BelaboxRecentPoint[]): string => JSON.stringify(
  recent.map(({ at, bitrateKbps, rttMs, connected }) => [at, bitrateKbps, rttMs, connected]),
);

const encodedPhase = (phase: BelaboxFetchPhase): string => JSON.stringify({
  consecutiveFailures: phase.consecutiveFailures,
  fetchFailing: phase.failing,
  ...(phase.lastAttemptAt === undefined ? {} : { lastAttemptAt: phase.lastAttemptAt }),
});

export const belaboxHistoryStatusGuard = `WHERE EXISTS (
    SELECT 1 FROM belabox_status
     WHERE channel_id = ? AND revision = ? AND history_sample_json = ?
       AND stream_id = ? AND belabox_stream_id = ?
  ) AND EXISTS (
    SELECT 1 FROM channel_modules
     WHERE channel_id = ? AND module_id = 'belabox' AND enabled = 1
       AND revision = ? AND json_extract(settings, '$.mode') = 'interval'
  ) AND ${belaboxStreamStateSnapshotGuard}`;

/** Clears an incomplete poll's baseline only for the lifecycle snapshot the alarm observed. */
export const clearBelaboxHistoryBaseline = async (
  db: D1Database,
  channelId: string,
  expectedStatusRevision: number,
  expectedModuleRevision: number | null,
  expectedStreamSnapshot: BelaboxStreamStateSnapshot | null,
): Promise<boolean> => {
  const result = await db.prepare(
    `UPDATE belabox_status
        SET history_sample_json = NULL,
            history_module_revision = NULL,
            revision = revision + 1
      WHERE channel_id = ? AND revision = ?
        AND (history_sample_json IS NOT NULL OR history_module_revision IS NOT NULL)
        AND ${belaboxHistoryModuleRevisionGuard}
        AND ${belaboxStreamStateSnapshotGuard}`,
  ).bind(
    channelId,
    expectedStatusRevision,
    expectedModuleRevision,
    channelId,
    channelId,
    expectedModuleRevision,
    ...streamSnapshotBindings(channelId, expectedStreamSnapshot),
  ).run();
  return result.meta.changes > 0;
};

export const markBelaboxPollingStarted = async (
  db: D1Database,
  channelId: string,
): Promise<boolean> => {
  const result = await db.prepare(
    `INSERT INTO belabox_status
      (channel_id, polling, stream_id, belabox_stream_id, fetch_phase_json, recent_json, revision)
     VALUES (?, 1, NULL, NULL, ?, '[]', 1)
     ON CONFLICT (channel_id) DO UPDATE SET
       polling = 1,
       revision = belabox_status.revision + 1
     WHERE belabox_status.polling != 1`,
  ).bind(channelId, encodedPhase(EMPTY_FETCH_PHASE)).run();
  return result.meta.changes > 0;
};

export const writeBelaboxFetch = async (
  db: D1Database,
  input: {
    channelId: string;
    sample: BelaboxSample | null;
    errorCode: BelaboxFetchFailureReason | null;
    polling: boolean;
    streamId: string | null;
    streamSessionKey: string | null;
    belaboxStreamId: string | null;
    fetchPhase: BelaboxFetchPhase;
    alertState: BelaboxAlertState;
    recent: readonly BelaboxRecentPoint[];
    expectedSecretVersion: string;
    expectedStatusRevision: number | null;
    expectedStreamSession?: BelaboxStreamSession | null;
    expectedModuleRevision?: number | null;
    expectedModuleMode?: "interval" | "on_demand";
    expectedStreamSnapshot?: BelaboxStreamStateSnapshot | null;
    finalizeNoncurrentStreams?: boolean;
    beforeSampleWrites?: readonly D1PreparedStatement[];
    historySample?: BelaboxSample | null;
    resetHistoryBaseline?: boolean;
    historyModuleRevision?: number | null;
    history?: {
      streamId: string;
      startedAt: string;
      minuteAt: string;
      droppedDelta: number;
      lowSeconds: number;
      disconnectedSeconds: number;
      disconnectCount: number;
    };
  },
): Promise<{ revision: number; sample: BelaboxSample | null } | null> => {
  const sessionGuard = belaboxStreamSessionGuard(input.channelId, input.expectedStreamSession);
  const sampleJson = input.sample === null ? null : JSON.stringify(input.sample);
  const historySampleJson = input.history === undefined ? null : JSON.stringify(input.historySample ?? input.sample);
  const historyModuleRevision = input.history === undefined ? null : input.historyModuleRevision ?? null;
  const statusWrite = db.prepare(
    `INSERT INTO belabox_status
      (channel_id, sampled_at, sample_json, error_code, polling, stream_id, stream_session_key, belabox_stream_id,
       fetch_phase_json, recent_json, alert_json, history_sample_json, history_module_revision, revision)
     SELECT ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, 1
     WHERE EXISTS (
       SELECT 1 FROM module_secrets
        WHERE channel_id = ? AND module_id = ? AND name = ? AND ciphertext = ?
     )
       AND (? IS NULL OR EXISTS (
         SELECT 1 FROM belabox_status
          WHERE channel_id = ? AND revision = ?
       ))
       AND (? IS NULL OR EXISTS (
         SELECT 1 FROM channel_modules
          WHERE channel_id = ? AND module_id = 'belabox' AND enabled = 1
            AND revision = ? AND json_extract(settings, '$.mode') = ?
       ))
       AND (? = 0 OR ${belaboxStreamStateSnapshotGuard})
       AND (? = 0 OR NOT EXISTS (
         SELECT 1 FROM belabox_streams
          WHERE channel_id = ? AND ended_at IS NULL AND stream_id IS NOT ?
       ))
       ${sessionGuard.sql}
     ON CONFLICT (channel_id) DO UPDATE SET
       sampled_at = CASE
         WHEN belabox_status.stream_session_key IS NOT excluded.stream_session_key
         THEN excluded.sampled_at
         WHEN excluded.sampled_at IS NOT NULL AND
              (belabox_status.sampled_at IS NULL OR excluded.sampled_at > belabox_status.sampled_at)
         THEN excluded.sampled_at ELSE belabox_status.sampled_at END,
       sample_json = CASE
         WHEN belabox_status.stream_session_key IS NOT excluded.stream_session_key
         THEN excluded.sample_json
         WHEN excluded.sampled_at IS NOT NULL AND
              (belabox_status.sampled_at IS NULL OR excluded.sampled_at > belabox_status.sampled_at)
         THEN excluded.sample_json ELSE belabox_status.sample_json END,
       error_code = excluded.error_code,
       polling = excluded.polling,
       stream_id = excluded.stream_id,
       stream_session_key = excluded.stream_session_key,
       belabox_stream_id = excluded.belabox_stream_id,
       fetch_phase_json = excluded.fetch_phase_json,
       recent_json = excluded.recent_json,
       alert_json = excluded.alert_json,
       history_sample_json = CASE
         WHEN ? = 1 THEN excluded.history_sample_json
         WHEN ? = 1 THEN NULL
         ELSE belabox_status.history_sample_json
       END,
       history_module_revision = CASE
         WHEN ? = 1 THEN excluded.history_module_revision
         WHEN ? = 1 THEN NULL
         ELSE belabox_status.history_module_revision
       END,
       revision = belabox_status.revision + 1
     WHERE belabox_status.revision = ?
     RETURNING revision, sample_json`,
  ).bind(
    input.channelId,
    input.sample?.at ?? null,
    sampleJson,
    input.errorCode,
    Number(input.polling),
    input.streamId,
    input.streamSessionKey,
    input.belaboxStreamId,
    encodedPhase(input.fetchPhase),
    encodedRecent(input.recent),
    JSON.stringify(input.alertState),
    historySampleJson,
    historyModuleRevision,
    input.channelId,
    BELABOX_MODULE_ID,
    BELABOX_STATS_URL_SECRET,
    input.expectedSecretVersion,
    input.expectedStatusRevision,
    input.channelId,
    input.expectedStatusRevision,
    input.expectedModuleRevision ?? null,
    input.channelId,
    input.expectedModuleRevision ?? null,
    input.expectedModuleMode ?? "interval",
    Number(input.expectedStreamSnapshot !== undefined),
    ...streamSnapshotBindings(input.channelId, input.expectedStreamSnapshot ?? null),
    Number(input.finalizeNoncurrentStreams === true),
    input.channelId,
    input.streamId,
    ...sessionGuard.values,
    Number(input.history !== undefined),
    Number(input.resetHistoryBaseline === true),
    Number(input.history !== undefined),
    Number(input.resetHistoryBaseline === true),
    input.expectedStatusRevision ?? -1,
  );
  const hasHistory = input.history !== undefined && input.sample !== null && sampleJson !== null && historySampleJson !== null;
  const nextRevision = (input.expectedStatusRevision ?? 0) + 1;
  const history = input.history;
  const sample = input.sample;
  const historyWrites: D1PreparedStatement[] = [];
  if (hasHistory && history !== undefined && sample !== null) {
    const minuteWrite = db.prepare(
      `INSERT INTO belabox_minutes
        (channel_id, minute_at, stream_id, samples, connected_samples, bitrate_min, bitrate_max,
         bitrate_sum, rtt_max, rtt_sum, dropped_delta)
       SELECT ?, ?, ?, 1, ?, ?, ?, ?, ?, ?, ?
       ${belaboxHistoryStatusGuard}
       ON CONFLICT (channel_id, minute_at, stream_id) DO UPDATE SET
         samples = belabox_minutes.samples + 1,
         connected_samples = belabox_minutes.connected_samples + excluded.connected_samples,
         bitrate_min = MIN(belabox_minutes.bitrate_min, excluded.bitrate_min),
         bitrate_max = MAX(belabox_minutes.bitrate_max, excluded.bitrate_max),
         bitrate_sum = belabox_minutes.bitrate_sum + excluded.bitrate_sum,
         rtt_max = MAX(belabox_minutes.rtt_max, excluded.rtt_max),
         rtt_sum = belabox_minutes.rtt_sum + excluded.rtt_sum,
         rolled_up_at = NULL,
         dropped_delta = belabox_minutes.dropped_delta + excluded.dropped_delta`,
    ).bind(
      input.channelId,
      history.minuteAt,
      history.streamId,
      Number(sample.connected),
      sample.bitrateKbps,
      sample.bitrateKbps,
      sample.bitrateKbps,
      sample.rttMs,
      sample.rttMs,
      history.droppedDelta,
      input.channelId,
      nextRevision,
      historySampleJson,
      history.streamId,
      history.streamId,
      input.channelId,
      input.expectedModuleRevision ?? null,
      ...streamSnapshotBindings(input.channelId, input.expectedStreamSnapshot ?? null),
    );
    const streamWrite = db.prepare(
      `INSERT INTO belabox_streams
        (channel_id, stream_id, started_at, ended_at, samples, bitrate_avg, bitrate_p10,
         low_seconds, disconnected_seconds, disconnect_count, dropped_total)
       SELECT ?, ?, ?, NULL, 1, ?, NULL, ?, ?, ?, ?
       ${belaboxHistoryStatusGuard}
       ON CONFLICT (channel_id, stream_id) DO UPDATE SET
         samples = belabox_streams.samples + 1,
         bitrate_avg = ((belabox_streams.bitrate_avg * belabox_streams.samples) + excluded.bitrate_avg) /
                       (belabox_streams.samples + 1),
         low_seconds = belabox_streams.low_seconds + excluded.low_seconds,
         disconnected_seconds = belabox_streams.disconnected_seconds + excluded.disconnected_seconds,
         disconnect_count = belabox_streams.disconnect_count + excluded.disconnect_count,
         dropped_total = belabox_streams.dropped_total + excluded.dropped_total
       WHERE belabox_streams.ended_at IS NULL`,
    ).bind(
      input.channelId,
      history.streamId,
      history.startedAt,
      sample.bitrateKbps,
      history.lowSeconds,
      history.disconnectedSeconds,
      history.disconnectCount,
      history.droppedDelta,
      input.channelId,
      nextRevision,
      historySampleJson,
      history.streamId,
      history.streamId,
      input.channelId,
      input.expectedModuleRevision ?? null,
      ...streamSnapshotBindings(input.channelId, input.expectedStreamSnapshot ?? null),
    );
    historyWrites.push(minuteWrite, streamWrite);
  }
  const beforeSampleWrites = [...(input.beforeSampleWrites ?? [])];
  const statusResultIndex = beforeSampleWrites.length;
  const results = await db.batch([...beforeSampleWrites, statusWrite, ...historyWrites]);
  const statusResult = results[statusResultIndex];
  if ((statusResult?.meta.changes ?? 0) === 0) return null;
  const returnedStatus = statusResult?.results[0] as { revision?: unknown; sample_json?: unknown } | undefined;
  const writtenStatus = returnedStatus ?? await db.prepare(
    "SELECT revision, sample_json FROM belabox_status WHERE channel_id = ?",
  ).bind(input.channelId).first<{ revision: number; sample_json: string | null }>();
  if (writtenStatus === null) return null;
  return {
    revision: typeof writtenStatus.revision === "number" ? writtenStatus.revision : nextRevision,
    sample: parseSample(typeof writtenStatus.sample_json === "string" ? writtenStatus.sample_json : null),
  };
};

export const writeBelaboxAlertState = async (
  db: D1Database,
  channelId: string,
  alertState: BelaboxAlertState,
  expectedRevision: number,
): Promise<number | null> => {
  const row = await db.prepare(
    `UPDATE belabox_status
        SET alert_json = ?, revision = revision + 1
      WHERE channel_id = ? AND revision = ?
      RETURNING revision`,
  ).bind(JSON.stringify(alertState), channelId, expectedRevision).first<{ revision: number }>();
  return row?.revision ?? null;
};

export const clearBelaboxSampleForPoll = async (
  db: D1Database,
  channelId: string,
  expectedStatusRevision: number,
  expectedStreamSnapshot: BelaboxStreamStateSnapshot | null,
): Promise<boolean> => {
  const result = await db.prepare(
  `UPDATE belabox_status
      SET sampled_at = NULL,
          sample_json = NULL,
          error_code = NULL,
          stream_session_key = NULL,
          belabox_stream_id = NULL,
          fetch_phase_json = ?,
          recent_json = '[]',
          alert_json = '{}',
          revision = revision + 1
    WHERE channel_id = ? AND revision = ? AND ${belaboxStreamStateSnapshotGuard}`,
  ).bind(
    encodedPhase(EMPTY_FETCH_PHASE),
    channelId,
    expectedStatusRevision,
    ...streamSnapshotBindings(channelId, expectedStreamSnapshot),
  ).run();
  return result.meta.changes > 0;
};
